"use client";

import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useReducedMotion } from "framer-motion";

export const HeroTitle = ({
  title,
  highlight,
}: {
  title: string;
  highlight?: React.ReactNode;
}) => {
  return (
    <h1
      className={clsx(
        "text-5xl",
        "sm:text-6xl",
        "md:text-7xl",
        "lg:text-8xl",
        "font-martina",
        "font-normal",
        "mx-auto",
        "text-center",
        "tracking-tight",
        "leading-[1.05]",
        "px-4",
        "md:px-0",
        "max-w-6xl",
      )}
    >
      {title}
      {highlight && (
        <>
          {" "}
          <br className="hidden sm:block" />
          <span className="text-gail-primary">{highlight}</span>
        </>
      )}
    </h1>
  );
};

const TYPING_SPEED_MS = 85;
const DELETING_SPEED_MS = 45;
const HOLD_DURATION_MS = 2200;
const NEXT_PHRASE_DELAY_MS = 350;

type TypewriterPhase = "typing" | "holding" | "deleting";

export const TypewriterHighlight = ({ phrases }: { phrases: string[] }) => {
  const shouldReduceMotion = useReducedMotion();
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [phase, setPhase] = useState<TypewriterPhase>("typing");
  const phrasesKey = phrases.join("\u0000");
  const safePhrases = useMemo(
    () => (phrasesKey ? phrasesKey.split("\u0000") : [""]),
    [phrasesKey],
  );
  const currentPhrase = safePhrases[phraseIndex] ?? "";
  const longestPhrase = useMemo(
    () =>
      safePhrases.reduce(
        (longest, phrase) => (phrase.length > longest.length ? phrase : longest),
        safePhrases[0] ?? "",
      ),
    [safePhrases],
  );

  useEffect(() => {
    if (shouldReduceMotion) {
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      if (phase === "typing") {
        if (displayText === currentPhrase) {
          setPhase("holding");
          return;
        }

        setDisplayText(currentPhrase.slice(0, displayText.length + 1));
        return;
      }

      if (phase === "holding") {
        setPhase("deleting");
        return;
      }

      if (displayText.length === 0) {
        setPhraseIndex((currentIndex) => (currentIndex + 1) % safePhrases.length);
        setPhase("typing");
        return;
      }

      setDisplayText(currentPhrase.slice(0, displayText.length - 1));
    }, getAnimationDelay(phase, displayText.length));

    return () => globalThis.clearTimeout(timeout);
  }, [currentPhrase, displayText, phase, safePhrases.length, shouldReduceMotion]);

  return (
    <span className="relative inline-grid align-baseline">
      <span
        className="pointer-events-none invisible col-start-1 row-start-1 select-none"
        aria-hidden="true"
      >
        {longestPhrase}
      </span>
      <span className="col-start-1 row-start-1 text-gail-primary" aria-hidden="true">
        {shouldReduceMotion ? currentPhrase : displayText}
        {!shouldReduceMotion && (
          <span className="animate-pulse" aria-hidden="true">
            |
          </span>
        )}
      </span>
      <span className="sr-only">
        {shouldReduceMotion ? currentPhrase : displayText || currentPhrase}
      </span>
    </span>
  );
};

function getAnimationDelay(phase: TypewriterPhase, displayLength: number) {
  if (phase === "holding") {
    return HOLD_DURATION_MS;
  }

  if (phase === "deleting") {
    return displayLength === 0 ? NEXT_PHRASE_DELAY_MS : DELETING_SPEED_MS;
  }

  return TYPING_SPEED_MS;
}

export const HeroDescription = ({ description }: { description: string }) => {
  return (
    <p
      className={clsx(
        "text-lg",
        "md:text-2xl",
        "max-w-md",
        "lg:max-w-sm",
        "mx-auto",
        "text-center",
        "px-12",
        "lg:px-0",
        "lg:mt-0",
        "mt-2",
        "mb-8",
        "lg:mb-0",
        "leading-tight",
        "md:max-w-xl",
        "md:mt-8",
      )}
    >
      {description}
    </p>
  );
};

export const BackgroundImage = () => {
  return (
    <div className="absolute inset-0 z-0 bg-[#BF730033]">
      <svg
        className="absolute inset-0 w-full h-full opacity-40 mix-blend-multiply"
        aria-hidden="true"
        focusable="false"
      >
        <filter id="grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.45"
            numOctaves="3"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grain)" />
      </svg>
    </div>
  );
};

// Type definition for AI types
export type AiType = {
  label: string;
  key: string;
  index?: number;
};
